import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { Avatar } from "./Avatar";
import { ConfirmRemoveButton } from "./ConfirmRemoveButton";

// <AssigneeRow> — Avatar + display name + the two-step ✕ remove idiom, for a
// list of "who this program/plan is assigned to" (PE-48). Net-new primitive:
// there was no "person row with a remove control" primitive before this
// (grep confirmed) — the two nearest siblings (mobile's CoachEdgesSection,
// web-rnw's CoachSettingsScreen) each hand-roll their own row instead of
// sharing one, and PE-48 is deliberately NOT touching either (out of scope,
// pre-existing divergence). Its one real consumer, ComposerAssigneesBand,
// renders it four times per mount (self, each assignee, each pending email,
// each roster candidate) for both the program and the plan path — repeated
// use INSIDE one file, which is DRY, not a second callsite.
//
// `removable: false` renders the name alone (no remove control) — the
// caller (never self, per program.assignSelf / plans.assignSelf) rather than
// a disabled/greyed button, which ConfirmRemoveButton has no mode for. The
// discriminated union means a non-removable row can't be constructed with a
// dangling onRemove/removeLabel nobody will call.
//
// `spokenRestLabel`/`spokenConfirmLabel` name WHO is being removed for a
// screen reader: `removeLabel`/`confirmLabel` alone read identically on
// every row ("Remove, button"), so with two-plus assignees there is no way
// to tell which one is about to go. The caller composes them from its own
// translated label + this row's `name` (mirrors how the roster's "Add"
// button already gets a per-candidate accessibilityLabel).
type AssigneeRowBase = {
  theme: UiTheme;
  /** Resolved display name, or the raw sub/email while unresolved — the
   *  caller decides the fallback (mirrors useCoachDashboard's own
   *  `name ?? fallbackName` pattern; this primitive stays presentation-only). */
  name: string;
  /** Small caption under the name — e.g. "Pending" for an email not yet
   *  resolved to a sub. Never colour-only: this is the non-colour cue
   *  distinguishing "assigned" from "pending". */
  caption?: string;
};

export type AssigneeRowProps =
  | (AssigneeRowBase & {
      removable: true;
      onRemove: () => void;
      removeLabel: string;
      confirmLabel: string;
      confirmHint?: string;
      /** Announced instead of `removeLabel`/`confirmLabel` alone, so a
       *  screen reader says WHO — e.g. `${removeLabel} ${name}`. Without
       *  these, every row on a multi-assignee list announces identically. */
      spokenRestLabel?: string;
      spokenConfirmLabel?: string;
    })
  | (AssigneeRowBase & { removable: false });

export function AssigneeRow(props: AssigneeRowProps) {
  const { theme, name, caption } = props;
  const styles = makeStyles(theme);
  return (
    <View style={styles.row}>
      <Avatar theme={theme} name={name} size={32} />
      <View style={styles.textCol}>
        <Text style={styles.name} numberOfLines={1}>
          {name}
        </Text>
        {caption ? (
          <Text style={styles.caption} numberOfLines={1}>
            {caption}
          </Text>
        ) : null}
      </View>
      {props.removable ? (
        <ConfirmRemoveButton
          onRemove={props.onRemove}
          style={styles.removeBtn}
          textStyle={styles.removeBtnText}
          removeLabel={props.removeLabel}
          confirmLabel={props.confirmLabel}
          confirmHint={props.confirmHint}
          spokenRestLabel={props.spokenRestLabel}
          spokenConfirmLabel={props.spokenConfirmLabel}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      // s2 (4px) left two 44pt ✕ targets on adjacent rows ~8px apart —
      // under the 2×hitSlop(8)=16px floor ConfirmRemoveButton's own header
      // names for adjacent destructive controls. s3 doubles the gap to 16px.
      paddingVertical: s.s3,
    },
    textCol: { flex: 1, minWidth: 0, gap: 1 },
    name: { color: p.ink, fontSize: t.sizes.base },
    // sm (12px), not xs (11px): this caption is the ONLY non-colour cue
    // telling a pending email apart from an active assignee, so it can't be
    // the smallest text on the surface.
    caption: { color: p.ink3, fontSize: t.sizes.sm },
    removeBtn: { minHeight: 40, paddingHorizontal: s.s2, justifyContent: "center" },
    removeBtnText: { color: p.danger, fontSize: t.sizes.sm, fontWeight: "700" },
  });
}
